import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { getRepo, type MediaItem } from '../../lib/repo';
import { useAsync } from '../../lib/hooks';
import { relativeTime } from '../../lib/format';
import { Button, Card, EmptyState, Field, Input, Spinner, useToast } from '../../components/ui';

const MAX_BYTES = 25 * 1024 * 1024; // matches the DB constraint (25 MiB)

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}

interface Meta { altText: string; credit: string }

export function MediaPage() {
  const toast = useToast();
  const { data, loading, error, reload } = useAsync<MediaItem[]>(() => getRepo().listMedia(), []);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [drafts, setDrafts] = useState<Record<string, Meta>>({});
  const items = data ?? [];

  async function onPick(files: FileList | null) {
    const file = files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast('Only image uploads are allowed', 'error'); return; }
    if (file.size > MAX_BYTES) { toast('File is larger than 25 MB', 'error'); return; }

    setBusy(true);
    try {
      // Demo mode: the data URL stands in for a Supabase Storage public URL.
      // In production this would upload to the `media` bucket first.
      const url = await readAsDataUrl(file);
      await getRepo().addMedia(
        { fileName: file.name, url, mimeType: file.type, sizeBytes: file.size, altText: '', credit: '' },
      );
      toast('Uploaded');
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Upload failed', 'error');
    } finally {
      setBusy(false);
    }
  }

  function metaFor(item: MediaItem): Meta {
    return drafts[item.id] ?? { altText: item.altText, credit: item.credit };
  }

  /** Persist every edited item, then re-read the library so the UI shows stored values. */
  async function saveAll() {
    const entries = Object.entries(drafts);
    if (!entries.length) return;
    setSaving(true);
    setSaveError('');
    try {
      for (const [id, meta] of entries) {
        await getRepo().updateMedia(id, { altText: meta.altText, credit: meta.credit });
      }
      toast(`Saved ${entries.length} item${entries.length === 1 ? '' : 's'}`);
      setDrafts({});
      reload();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Could not save the media details.');
      toast('Could not save the media details', 'error');
    } finally {
      setSaving(false);
    }
  }

  const filled = Object.keys(drafts).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Media library</h1>
          <p className="mt-1 text-sm text-muted">{items.length} files · images up to 25 MB.</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => void onPick(e.target.files)} aria-label="Upload file" />
          <Button onClick={() => fileRef.current?.click()} disabled={busy}>
            <Upload className="h-4 w-4" /> {busy ? 'Uploading…' : 'Upload'}
          </Button>
        </div>
      </div>

      {error ? <p className="text-sm text-danger">{error}</p> : null}
      {loading && !items.length ? <Spinner label="Loading media…" /> : null}

      {!loading && items.length === 0 ? (
        <EmptyState
          title="No media yet"
          description="Upload images to use as cover art, inline images and social cards."
          action={<Button onClick={() => fileRef.current?.click()}>Upload the first image</Button>}
        />
      ) : null}

      {items.length ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((m) => {
            const meta = metaFor(m);
            return (
              <Card key={m.id} className="overflow-hidden">
                <img src={m.url} alt={meta.altText || m.fileName} className="aspect-[4/3] w-full bg-background object-cover" loading="lazy" />
                <div className="space-y-3 p-4">
                  <div>
                    <p className="truncate text-sm font-medium" title={m.fileName}>{m.fileName}</p>
                    <p className="text-xs text-muted">{m.mimeType} · {humanSize(m.sizeBytes)} · {relativeTime(m.createdAt)}</p>
                  </div>
                  <Field label="Alt text" hint={`${meta.altText.length}/250`}>
                    <Input
                      value={meta.altText}
                      maxLength={250}
                      placeholder="Describe the image for screen readers"
                      onChange={(e) => setDrafts((d) => ({ ...d, [m.id]: { ...meta, altText: e.target.value } }))}
                    />
                  </Field>
                  <Field label="Credit">
                    <Input
                      value={meta.credit}
                      maxLength={160}
                      placeholder="Photographer / source"
                      onChange={(e) => setDrafts((d) => ({ ...d, [m.id]: { ...meta, credit: e.target.value } }))}
                    />
                  </Field>
                </div>
              </Card>
            );
          })}
        </div>
      ) : null}

      {filled > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
          <div>
            <p className="text-sm text-muted">
              {filled} item{filled === 1 ? '' : 's'} with unsaved alt text or credit.
            </p>
            {saveError ? <p className="mt-1 text-sm text-danger">{saveError}</p> : null}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setSaveError(''); setDrafts({}); }} disabled={saving}>Discard</Button>
            <Button onClick={() => void saveAll()} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
